<template>
  <div class="living-people-category">
    <div class="category-header">
      <ExpandButton
        :hidden="!props.persons.length"
        v-model:expanded="expandedRef"/>
      <Checkbox
        v-model:checked="selectedRef"
        :disabled="disabledRef"/>
      <div role="button" @click="toggleExpandList">
        {{ getRelationshipCategoryText(props.category) }}
        <span class="category-count">({{ props.persons.length }})</span>
      </div>
    </div>
    <ul ref="listElemRef" class="list-unstyled collapse">
      <li v-for="p in props.persons" :key="p.id">
        <LivingPersonListItem :person="p" :session-id="sessionId"/>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.category-header {
  margin-bottom: 0.25rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;

  .checkbox {
  }

  div[role="button"] {
    cursor: pointer;
  }

  .category-count {
    font-size: 0.875rem;
    color: #aaa;
  }
}

ul {
  margin-left: 2rem;

  li {
    margin-bottom: 0.25rem;
  }
}
</style>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { Person } from '@webapp/rd/ResearchDataModel'
import { Collapse } from 'bootstrap'
import { remove } from 'lodash'
import { RelationshipCategory, getRelationshipCategoryText } from './LivingWindowGroups'
import { useSyncSessionStore } from './SyncSessionStore'
import ExpandButton from '@webapp/util/ExpandButton.vue'
import Checkbox from '@webapp/util/Checkbox.vue'
import LivingPersonListItem from './LivingPersonListItem.vue'

const props = defineProps<{
  category: RelationshipCategory
  persons: Person[]
  sessionId: string
}>()

const syncSessionStore = useSyncSessionStore()
const stateRef = computed(() => syncSessionStore.getState(props.sessionId))

const selectedPersonIdsRef = computed(() => {
  const ids = new Set<string>()
  const graph = stateRef.value?.sourceGraph
  if (graph) {
    for (const id of graph.mappedPersonIds) {
      ids.add(id)
    }
  }
  for (const id of stateRef.value?.livingWindowPersonIds ?? []) {
    ids.add(id)
  }
  return ids
})

const expandedRef = ref(false)
const listElemRef = ref<HTMLElement>()

const selectedRef = computed({
  get: () => {
    if (!props.persons?.length) {
      return false
    }
    const selectedCount = props.persons.filter(p => selectedPersonIdsRef.value.has(p.id!)).length
    const totalCount = props.persons.length
    return selectedCount == 0 ? false : selectedCount == totalCount ? true : undefined
  },
  set: value => {
    const includeIds = stateRef.value?.livingWindowPersonIds
    if (!includeIds) {
      return
    }
    if (value) {
      const alreadySelected = selectedPersonIdsRef.value
      const alreadyIncluded = new Set(includeIds)
      props.persons
        .filter(p => !alreadySelected.has(p.id!) && !alreadyIncluded.has(p.id!))
        .forEach(p => includeIds.push(p.id!))
    }
    else {
      const idsToRemove = new Set(props.persons.map(p => p.id!))
      remove(includeIds, id => idsToRemove.has(id))
    }
  },
})

const disabledRef = computed(() => {
  const mapped = stateRef.value?.sourceGraph?.mappedPersonIds
  return !props.persons.some(p => !mapped?.has(p.id!))
})

function toggleExpandList() {
  if (props.persons.length > 0) {
    expandedRef.value = !expandedRef.value
  }
}

onMounted(() => listElemRef.value?.addEventListener('shown.bs.collapse', onShown))
onUnmounted(() => listElemRef.value?.removeEventListener('shown.bs.collapse', onShown))

watch(expandedRef, () => {
  const c = Collapse.getOrCreateInstance(listElemRef.value!)
  c.toggle()
})

function onShown() {
  const nthItem = listElemRef.value?.querySelector('li:nth-child(5)')
  const lastItem = listElemRef.value?.querySelector('li:last-child')
  const scrollToItem = nthItem ?? lastItem
  if (scrollToItem) {
    scrollToItem.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    })
  }
}
</script>
